import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Logo } from "@/components/Logo";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const guestInterviewId = params.get("interview") || localStorage.getItem("guest_interview_id");
  const nextPath = params.get("next");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const invitationToken = params.get("invite") || null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!agree) { setErr("Please accept the Terms and Privacy Policy to continue."); return; }
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await register(email, password, name, guestInterviewId, { marketingConsent: marketing, invitationToken });
      localStorage.removeItem("guest_interview_id");
      track("sign_up");
      toast.success("Account created. Welcome to the honest side.");
      navigate(nextPath || "/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-donkey-bg grain flex flex-col">
      <div className="p-6"><Logo /></div>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md card-donkey rounded-2xl p-8 animate-fade-up">
          <DonkeyMascot size={96} alt="" className="mx-auto mb-4 opacity-95 drop-shadow-[0_0_40px_rgba(249,115,22,0.22)]" />
          <h1 className="font-heading font-bold text-2xl text-donkey-warm">Create your account</h1>
          <p className="text-sm text-donkey-grey mt-2">
            {guestInterviewId ? "You'll keep the interview you just started." : "Save your reports and unlock verdicts."}
          </p>
          <form onSubmit={submit} className="mt-7 space-y-5">
            <div>
              <Label htmlFor="name" className="text-donkey-warm/90">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1.5 bg-donkey-bg border-white/15 text-donkey-warm" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email" className="text-donkey-warm/90">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-donkey-bg border-white/15 text-donkey-warm" data-testid="register-email" />
            </div>
            <div className="relative">
              <Label htmlFor="password" className="text-donkey-warm/90">Password</Label>
              <Input id="password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-donkey-bg border-white/15 text-donkey-warm pr-10" data-testid="register-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                aria-pressed={showPw} data-testid="register-password-toggle"
                className="absolute right-3 top-9 text-donkey-grey hover:text-donkey-warm">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <p className="text-xs text-donkey-grey mt-1">At least 8 characters.</p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={agree} onCheckedChange={setAgree} aria-label="I accept the Terms and Privacy Policy" className="mt-0.5 border-white/30" data-testid="register-agree" />
              <span className="text-xs text-donkey-grey leading-relaxed">
                I accept the <Link to="/terms" className="text-donkey-orange underline hover:no-underline">Terms</Link> and{" "}
                <Link to="/privacy" className="text-donkey-orange underline hover:no-underline">Privacy Policy</Link>, and consent to my
                interview answers being processed to generate my report.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={marketing} onCheckedChange={setMarketing} aria-label="Email marketing consent (optional)" className="mt-0.5 border-white/30" data-testid="register-marketing" />
              <span className="text-xs text-donkey-grey leading-relaxed">
                <span className="text-donkey-warm/90">Optional:</span> I'd like occasional Honest Donkey product news and offers by email. I can unsubscribe anytime. (You don't need to tick this to use the service.)
              </span>
            </label>
            {err && <p className="text-sm text-donkey-red" data-testid="register-error">{err}</p>}
            <Button type="submit" disabled={loading}
              className="w-full bg-donkey-orange text-donkey-black hover:bg-donkey-orange/90 font-semibold h-11"
              data-testid="register-submit">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-donkey-grey mt-6 text-center">
            Already have an account? <Link to="/login" className="text-donkey-orange underline hover:no-underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
